import React, { useState, useEffect } from "react";
import moment from "moment";

import { XIcon } from "@heroicons/react/outline";

import { useFormik } from "formik";
import * as yup from "yup";

import Dialog from "@/ui/Dialog";
import TextFieldFormik from "@/ui/TextFieldFormik";
import { TextAreaFormik } from "@/ui/TextArea";
import Button from "@/ui/Button";
import { CheckboxFormik } from "@/ui/Checkbox";
import { RadioGroupFormik } from "@/ui/RadioGroup";
import { readDocument, updateDocument, createEvent } from "@/lib/db";
import { useAuth } from "@/lib/auth";
import SignUpSignIn from "../user/SignUpSignIn";
import { DarkButton } from "../community/CTACards";
import { IconButton } from "@/ui/IconButton";
import { YouNeedToRegister, getContactFromCommunity, getReceiveEmails } from "./utils";
import { trackButtonClick } from "../user/UserUtils";

const updateLocalStorage = (user) => {
    const _name = localStorage.getItem("name");
    const _email = localStorage.getItem("email");
    const _phone = localStorage.getItem("phone");

    if (!_name && user.name) {
        localStorage.setItem("name", user.name);
    }

    if (!_email && user.email) {
        localStorage.setItem("email", user.email);
    }

    if (!_phone && user.phone) {
        localStorage.setItem("phone", user.phone);
    }
};

const readLocalStorage = () => {
    const name = localStorage.getItem("name");
    const email = localStorage.getItem("email");
    const phone = localStorage.getItem("phone");

    return { name, email, phone };
};

const TourRequest = ({ community }) => {
    const [open, setOpen] = useState(false);
    const auth = useAuth();
    const [user, setUser] = useState({});

    const userId = auth && auth.user ? auth.user.uid : "";
    const token = auth && auth.user ? auth.user.token : "";

    useEffect(() => {
        const getUser = async (uid) => {
            const _user = await readDocument("users", uid);
            updateLocalStorage(_user);
            setUser(_user);
        };

        if (userId) {
            getUser(userId);
        }
    }, [userId]);

    return (
        <div>
            <DarkButton
                className="animate-wiggle-cta animation-delay-2000"
                onClick={() => {
                    const value = trackButtonClick("request_tour_button");
                    gtag("event", "request_tour", {
                        event_category: "engagement",
                        event_label: "request_tour_button",
                        value,
                        item_id: community.nameId,
                    });

                    const engagementCount = trackButtonClick("engagement");
                    gtag("event", "engagementCountUpdated", {
                        engagement_count: engagementCount,
                    });

                    gtag("event", "conversion", {
                        send_to: "AW-10778046073/cgSQCO7AwJMaEPncr5Mo",
                    });

                    oaiq("measure", "appointment_scheduled", {
                        type: "customer_action",
                    });

                    createEvent({
                        nameId: community.nameId,
                        communityName: community.name,
                        uid: userId,
                        name: user?.name || "",
                        email: user?.email || "",
                        phone: user?.phone || "",
                        type: "clicked_request_tour_button",
                    });

                    setOpen(true);
                }}
            >
                Request a Tour
            </DarkButton>

            <TourRequestDialog community={community} open={open} setOpen={setOpen} />
        </div>
    );
};

export const TourRequestDialog = ({ community, open, setOpen }) => {
    const auth = useAuth();
    const [openSignUp, setOpenSignUp] = useState(false);
    const [openSignIn, setOpenSignIn] = useState(false);
    const [user, setUser] = useState({});
    const [complete, setComplete] = useState(false);
    const [email, setEmail] = useState("");
    const [initialValues, setInitialValues] = useState({});

    const userId = auth && auth.user ? auth.user.uid : "";
    const token = auth && auth.user ? auth.user.token : "";

    const contact = getContactFromCommunity(community);
    const communityEmails = getReceiveEmails(community);

    useEffect(() => {
        const getUser = async (uid) => {
            const _user = await readDocument("users", uid);
            updateLocalStorage(_user);
            setUser(_user);
        };

        if (userId) {
            getUser(userId);
        }
    }, [userId]);

    useEffect(() => {
        const values = readLocalStorage();
        setInitialValues(values);
    }, []);

    const handleClose = () => {
        setOpen(false);
        setComplete(false);
    };

    const handleComplete = async ({ createAccount, values }) => {
        const res = await fetch("/api/email/request-a-tour-claimed", {
            method: "post",
            headers: {
                "Content-Type": "application/json",
                token,
            },
            body: JSON.stringify(
                {
                    ...values,
                    title: `Tour Request - ${community.name}`,
                    preview: `Tour Request - ${community.name}`,
                    contact,
                    communityEmails,
                },
                null,
                2
            ),
        });

        updateLocalStorage(values);

        if (createAccount) {
            const { name, email } = values;

            const password = ""; // will be generated randomly
            const receivedEmailUpdates = true;
            const emailLink = true;

            try {
                await auth.signUp(email, password, name, receivedEmailUpdates, emailLink);

                gtag("event", "conversion", {
                    send_to: "AW-10778046073/z0kvCMDQspMaEPncr5Mo",
                });

                oaiq("measure", "registration_completed", {
                    type: "customer_action",
                });
            } catch (err) {
                console.error("Error creating account", err);

                if (emailLink && err.code === "auth/email-already-in-use") {
                    // sign in the user with email link
                    await auth.signInWithUserEmailLink(email);
                }
            }
        }

        createEvent({
            nameId: community.nameId,
            communityName: community.name,
            uid: userId,
            name: user?.name ? user.name : values.name,
            email: user?.email ? user.email : values.email,
            phone: user?.phone ? user.phone : values.phone,
            type: "sent_request_tour",
        });

        setComplete(true);
    };

    return (
        <div>
            <Dialog
                open={open}
                className="fixed inset-0 z-50 overflow-y-auto"
                onClose={handleClose}
            >
                <div className="min-h-screen px-4 text-center">
                    <Dialog.Overlay className="fixed -z-10 inset-0 bg-black opacity-70" />

                    {/* This element is to trick the browser into centering the modal contents. */}
                    <span className="inline-block h-screen align-middle" aria-hidden="true">
                        &#8203;
                    </span>

                    <div
                        className={`inline-block w-full max-w-lg pb-8 overflow-hidden
                                            text-left align-middle transition-all transform 
                                            bg-white shadow-xl rounded-2xl`}
                    >
                        <div className="flex justify-end"></div>

                        <Dialog.Title className="px-4 py-2 flex justify-between items-center bg-primary-500">
                            <div className="flex justify-center flex-grow">
                                <h3 className="text-xl font-bold font-headline uppercase text-white">
                                    Tour Request
                                </h3>
                            </div>
                            <IconButton onClick={handleClose}>
                                <XIcon className="w-6 h-6 text-white" />
                            </IconButton>
                        </Dialog.Title>

                        {/*userId === "" ? (
                            <YouNeedToRegister
                                setOpenSignIn={setOpenSignIn}
                                setOpenSignUp={setOpenSignUp}
                                onClose={handleClose}
                            />
                        ) : null*/}

                        {!complete ? (
                            <TourRequestForm
                                community={community}
                                user={user}
                                initialValues={initialValues}
                                onComplete={handleComplete}
                            />
                        ) : null}

                        {complete ? (
                            <TourRequestComplete community={community} email={email} />
                        ) : null}

                        {!user?.uid ? (
                            <div className="px-8 mt-2 text-sm flex space-x-2">
                                <div className="flex items-center space-x-2">
                                    <div>Already have an account?</div>
                                    <buton
                                        className="font-bold text-primary-500 underline hover:text-primary-400 hover:cursor-pointer"
                                        onClick={() => {
                                            handleClose();
                                            setOpenSignIn(true);
                                        }}
                                    >
                                        Sign In
                                    </buton>
                                </div>

                                <div className="flex items-center space-x-2">
                                    <div>or</div>

                                    <buton
                                        className="font-bold text-primary-500 underline hover:text-primary-400 hover:cursor-pointer"
                                        onClick={() => {
                                            handleClose();
                                            setOpenSignUp(true);
                                        }}
                                    >
                                        Sign Up
                                    </buton>

                                    <div>to create an account.</div>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            </Dialog>

            <SignUpSignIn
                openSignUp={openSignUp}
                openSignIn={openSignIn}
                setOpenSignUp={setOpenSignUp}
                setOpenSignIn={setOpenSignIn}
            />
        </div>
    );
};

const validationSchema = yup.object({
    name: yup.string().trim().required("Name is required").nullable(),
    email: yup
        .string()
        .trim()
        .required("Email is required")
        .email("Please enter a valid email")
        .nullable(),
    phone: yup.string().trim().required("Mobile Number is required").nullable(),
    message: yup.string().trim().required("Message is required").nullable(),
    tourDate: yup
        .date()
        .required("Date is required")
        .min(
            moment().add(1, "day").toDate(),
            "Date must start from " + moment().add(2, "day").format("MMM D")
        )
        .nullable(),
    tourTime: yup.string().required("Time is required").nullable(),
    tourType: yup.string().required("Select tour").nullable(),
    resident: yup.string().required("Select one option").nullable(),
});

const times = [
    { value: "9 am - 12 pm", name: "9 am - 12 pm" },
    { value: "12 pm - 4 pm", name: "12 pm - 4 pm" },
];

const TourRequestForm = ({ community, user, initialValues, onComplete }) => {
    // const [name, setName] = useState(user.name);
    // const [phone, setPhone] = useState(user.phone);
    // const [message, setMessage] = useState("");
    // const [urgent, setUrgent] = useState(false);

    const formik = useFormik({
        initialValues: {
            name: user?.name ? user.name : initialValues.name,
            email: user?.email ? user.email : initialValues.email,
            phone: user?.phone ? user.phone : initialValues.phone,
            tourDate: "",
            tourTime: "",
            tourType: "",
            resident: "",
            message: "",
            urgent: false,
        },
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            if (values.name !== user?.name || values.phone !== user?.phone) {
                try {
                    await updateDocument("users", user?.uid, {
                        ...user,
                        name: values.name ? values.name : user?.name,
                        phone: values.phone ? values.phone : user?.phone,
                    });
                } catch (err) {
                    console.error("Error updating user, maybe not logged in?", err);
                }
            }

            onComplete({
                createAccount: !user?.uid,
                values: {
                    name: values.name,
                    email: values.email,
                    phone: values.phone,
                    tourDate: values.tourDate,
                    tourTime: values.tourTime,
                    tourType: values.tourType,
                    resident: values.resident,
                    message: values.message,
                    urgent: values.urgent,
                    communityName: community.name,
                    communityUrl: community.nameId,
                },
            });
        },
    });

    return (
        <form onSubmit={formik.handleSubmit}>
            <div className="mt-2 sm:mt-4 sm:mb-0 px-4 sm:px-8">
                <TextFieldFormik
                    formik={formik}
                    id="name"
                    name="name"
                    label="Full Name"
                    fullwidth
                    inputClassName="bg-gray-100"
                />

                <TextFieldFormik
                    formik={formik}
                    id="email"
                    type="email"
                    name="email"
                    label="Email"
                    fullwidth
                    className="mt-2"
                />

                <TextFieldFormik
                    formik={formik}
                    id="phone"
                    name="phone"
                    label="Mobile Number"
                    fullwidth
                    inputClassName="bg-gray-100"
                    className="mt-2"
                />

                <div className="grid grid-cols-2 space-x-4">
                    <TextFieldFormik
                        formik={formik}
                        type="date"
                        id="tourDate"
                        name="tourDate"
                        label="Tour Date Requested"
                        fullwidth
                        inputClassName="bg-gray-100"
                        className="mt-2"
                        helperText={
                            "Starting from " + moment().add(2, "day").format("MMM D")
                        }
                    />

                    <TextFieldFormik
                        formik={formik}
                        select
                        id="tourTime"
                        name="tourTime"
                        label="Tour Time Requested"
                        fullwidth
                        inputClassName="bg-gray-100"
                        className="mt-2"
                        options={times}
                    />
                </div>

                <div className="grid grid-cols-2 space-x-4">
                    <RadioGroupFormik
                        formik={formik}
                        id="tourType"
                        name="tourType"
                        label="Tour Type"
                        className="mt-2 space-y-2"
                        required
                        options={[
                            { value: "In-Person Tour", name: "In-Person Tour" },
                            { value: "Live Video Tour", name: "Live Video Tour" },
                        ]}
                    />

                    <RadioGroupFormik
                        formik={formik}
                        id="resident"
                        name="resident"
                        label="Resident"
                        className="mt-2 space-y-2"
                        required
                        options={[
                            { value: "I am the Resident", name: "I am the Resident" },
                            {
                                value: "I am Helping a Resident",
                                name: "I am Helping a Resident",
                            },
                        ]}
                    />
                </div>

                <TextAreaFormik
                    formik={formik}
                    id="message"
                    name="message"
                    label="Message"
                    fullwidth
                    inputClassName="bg-gray-100"
                    className="mt-2"
                    textareaClassName="bg-gray-100"
                />

                <div className="mt-2 flex justify-between">
                    <Button className="" type="submit">
                        Submit Question{user?.uid ? "" : " and Create Account"}
                    </Button>
                    <CheckboxFormik
                        formik={formik}
                        id="urgent"
                        name="urgent"
                        label="Urgent Request"
                    />
                </div>
            </div>
        </form>
    );
};

const TourRequestComplete = ({ community, email }) => {
    return (
        <div className="mt-6 mb-4 px-16">
            <p className="text-lg text-center">Thank you for requesting a tour at</p>

            <p className="mt-2 text-xl text-primary-500 font-bold text-center">
                {community.name}
            </p>

            <p className="mt-4 text-center">
                Your tour request has been sent to this community.
                {/*paidPlan
                    ? "The community director or staff should contact you to confirm the tour within 2 days."
                    : "A Purple Door Finders representative will confirm the tour within 2 days."*/}
            </p>

            <hr className="mt-4 mx-24 border-black" />

            <p className="mt-8 text-sm font-bold text-center">
                If you do not receive a confirmation from the community within 24 hours,
                please reach out to them directly.
                {/*paidPlan
                    ? "If you do not hear from the community, please contact "
                    : "If you have any further questions, please contact "*/}
                {/*<a
                    href="mailto:support@purpledoorfinders.com"
                    className="text-primary-500 underline hover:font-bold"
                >
                    support@purpledoorfinders.com
                </a>*/}
            </p>

            {/*email ? <div dangerouslySetInnerHTML={{ __html: email }} /> : null*/}

            <p className="mt-4 text-sm font-bold text-center">
                Remember! Tell them Purple Door Finders sent you!
            </p>
        </div>
    );
};

export default TourRequest;
